import { z } from "zod";
import {
  firestoreTimestampSchema,
  hexColorSchema,
  userRoleEnum,
} from "./common.schema";

// ============================================
// USER ENTITY SCHEMA
// ============================================

export const userSchema = z.object({
  uid: z.string(), // Document ID (Firebase Auth UID)
  email: z.string().email(),
  role: userRoleEnum,
  companyId: z.string().optional(), // Required when role === 'employee' or 'doctor'
  displayName: z.string().min(1),

  // Doctor-specific fields (when role === 'doctor')
  specialization: z.string().optional(),
  color: hexColorSchema.optional(), // For UI visualization
  active: z.boolean().optional(),
  officeAddress: z.string().optional(),
  professionalRegisterNumber: z.string().optional(),

  createdAt: firestoreTimestampSchema.optional(),
  updatedAt: firestoreTimestampSchema.optional(),
  isAvailable: z.boolean().optional(),
  hasSignature: z.boolean().optional(),
  signatureUpdatedAt: firestoreTimestampSchema.optional(),

  geslabId: z.string().optional(),
  code: z.string().optional(),
});

export type User = z.infer<typeof userSchema>;

// ============================================
// CREATE USER DTO
// ============================================

export const createUserSchema = z
  .object({
    email: z.string().email("Valid email is required"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    role: userRoleEnum,
    companyId: z.string().optional(),
    displayName: z.string().min(1, "Display name is required"),

    // Doctor-specific fields
    specialization: z.string().optional(),
    color: hexColorSchema.optional(),
    active: z.boolean().optional(),
    officeAddress: z.string().optional(),
    professionalRegisterNumber: z.string().optional(),

    // Fields for creating a new company inline
    companyName: z.string().optional(),
    companyAddress: z.string().optional(),
    companyVatNumber: z.string().optional(),
    companyPrimaryDoctorId: z.string().optional(),
    companyCoordinationDoctorIds: z.array(z.string()).optional(),

    geslabId: z.string().optional(),
    code: z.string().optional(),
  })
  .refine(
    (data) => {
      // companyId required for employee or doctor roles (unless creating new company)
      if (data.role === "employee") {
        return data.companyId || data.companyName;
      }
      return true;
    },
    {
      message: "companyId is required when role is employee",
      path: ["companyId"],
    },
  );
/*  .refine(
    (data) => {
      // specialization required for doctor role
      if (data.role === 'doctor') {
        return !!data.specialization
      }
      return true
    },
    {
      message: 'specialization is required when role is doctor',
      path: ['specialization'],
    },
  )*/

export type CreateUser = z.infer<typeof createUserSchema>;

// ============================================
// UPDATE USER DTO
// ============================================

export const updateUserSchema = z.object({
  email: z.string().email().optional(),
  role: userRoleEnum.optional(),
  companyId: z.string().optional(),
  displayName: z.string().min(1).optional(),

  // Doctor-specific fields
  specialization: z.string().optional(),
  color: hexColorSchema.optional(),
  active: z.boolean().optional(),
  officeAddress: z.string().optional(),
  professionalRegisterNumber: z.string().optional(),
  geslabId: z.string().optional(),
  code: z.string().optional(),

  // Company fields for inline creation/update
  companyName: z.string().optional(),
  companyAddress: z.string().optional(),
  companyVatNumber: z.string().optional(),
  companyPrimaryDoctorId: z.string().optional(),
  companyCoordinationDoctorIds: z.array(z.string()).optional(),
  companyGeslabId: z.string().optional(),
});

export type UpdateUser = z.infer<typeof updateUserSchema>;

// ============================================
// UTILITY FUNCTION
// ============================================

export function createUserSearchName(name: string): string {
  return name.toLowerCase();
}
